import {isAfter, isBefore, startOfDay} from 'date-fns';
import * as yup from 'yup';

import {$invoiceStatusInfo} from '@invoice/models/common';

import {InvoiceTypeCodeStatus} from '@invoice/types/invoices';

import {$isNG} from '../../../models/utils';

const parseFormDate = (value: unknown): Date | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const d = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(d.getTime())) {
        return null;
    }
    return d;
};

const optionalDate = yup
    .date()
    .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null || originalValue === undefined) {
            return null;
        }
        return value;
    })
    .nullable();

const baseLabelValueSchema = yup
    .object({
        label: yup
            .string()
            .nullable()
            .notRequired(),
        value: yup
            .string()
            .nullable()
            .notRequired(),
    })
    .nullable()
    .notRequired();

const optionShape = baseLabelValueSchema.shape({
    city: yup
        .string()
        .nullable()
        .notRequired(),
    country: yup
        .string()
        .nullable()
        .notRequired(),
    postalCode: yup
        .string()
        .nullable()
        .notRequired(),
    street: yup
        .string()
        .nullable()
        .notRequired(),
});

export const generalInfoSchema = yup.object().shape({
    dueDate: optionalDate
        .when(['$IS_NG', '$DOC_TYPE'], {
            is: (isNG: boolean, docType: InvoiceTypeCodeStatus | undefined) =>
                isNG === false && docType !== InvoiceTypeCodeStatus.ANNULLING,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema,
        })
        .test('not-before-today', 'Due date cannot be earlier than today.', (value) => {
            const d = parseFormDate(value);
            if (!d) {
                return true;
            }
            return !isBefore(startOfDay(d), startOfDay(new Date()));
        }),
    issueDate: optionalDate
        .required()
        .test('not-after-today', 'Issue date cannot be later than today.', (value) => {
            const d = parseFormDate(value);
            if (!d) {
                return true;
            }
            return !isAfter(startOfDay(d), startOfDay(new Date()));
        }),
    customerCompanyNameOrTIN: optionShape.required(),
    bic: yup.string(),
    iban: yup.string().required(),
    customerEmail: yup
        .string()
        .email()
        .when('$IS_NG', {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema,
        }),
    customerPhoneNumber: yup.string(),
});

type ValidationContext = {
    IS_NG: boolean;
    DOC_TYPE: InvoiceTypeCodeStatus | undefined;
};

export const createValidationContext = (): ValidationContext => ({
    IS_NG: $isNG.getState(),
    DOC_TYPE: $invoiceStatusInfo.getState()?.docType,
});

const resolveFieldSchema = (fieldSchema: any, context: ValidationContext) => {
    if (!fieldSchema) {
        return null;
    }

    if (typeof fieldSchema.resolve === 'function') {
        return fieldSchema.resolve({
            context,
        });
    }

    return fieldSchema;
};

const hasRequiredTest = (schema: any) => {
    if (!schema) {
        return false;
    }

    // eslint-disable-next-line no-underscore-dangle
    const exclusiveTests = schema.exclusiveTests ?? schema._exclusive;
    if (exclusiveTests?.required) {
        return true;
    }

    const tests = schema.tests ?? [];
    return tests.some((test: any) => test?.OPTIONS?.name === 'required');
};

export const createIsFieldRequired = (context: ValidationContext) => {
    const resolvedSchema = generalInfoSchema.resolve({context});

    return (name: string) => {
        const fieldSchema = resolvedSchema.fields[name as keyof typeof resolvedSchema.fields];
        const concreteSchema = resolveFieldSchema(fieldSchema, context);

        return hasRequiredTest(concreteSchema);
    };
};
